import React, { useMemo } from 'react';
import { Typography, TextField, Icon, Button, BaseIcon, TextArea, Tooltip } from '@scale/scaleui';
import { ResumeScreeningConfig, ResumeScreeningJob } from '@scale/shared-types';
import { RadioGroup, Radio } from 'design-system';
import _ from 'lodash';

export const CreateAssistant: React.FC<{
  selectedJobConfig: ResumeScreeningConfig | Partial<ResumeScreeningJob>;
  handleInputChange: (
    e: React.ChangeEvent<HTMLInputElement> | React.ChangeEvent<HTMLTextAreaElement>,
    index?: number,
  ) => void;
  handleAddQuestion: () => void;
  handleRemoveQuestion: (index: number) => void;
  openJobConfigTestDialog?: (jobConfig: ResumeScreeningConfig) => void;
  isCreateAssistantOpen: boolean;
  toggleCreateAssistantOpen: () => void;
  handleRequiresAllQuestionsPassedChange: (value: boolean) => void;
  isOneOffJob?: boolean;
}> = ({
  selectedJobConfig,
  handleInputChange,
  handleAddQuestion,
  handleRemoveQuestion,
  openJobConfigTestDialog,
  isCreateAssistantOpen,
  toggleCreateAssistantOpen,
  handleRequiresAllQuestionsPassedChange,
  isOneOffJob = false,
}) => {
  const isTestDisabled = useMemo(() => {
    const hasNoQuestions = _.isEmpty(selectedJobConfig?.questionsToAskGPT);
    const hasEmptyQuestions =
      selectedJobConfig?.questionsToAskGPT?.some(q => q.trim() === '') ?? true;
    const noDescription = !selectedJobConfig?.assistantDescription;
    return hasNoQuestions || hasEmptyQuestions || noDescription;
  }, [selectedJobConfig]);

  return (
    <div className="scaleui scale-theme scale-tailwind">
      {!isOneOffJob && (
        <div className="flex flex-row cursor-pointer">
          <Icon
            onClick={toggleCreateAssistantOpen}
            icon={['fas', 'chevron-right']}
            className={`text-neutral-800 transition-transform duration-300 ${
              isCreateAssistantOpen ? 'rotate-90' : 'rotate-0'
            }`}
          />

          <Typography.H4 className="text-neutral-800">Create Your Resume Screen</Typography.H4>
        </div>
      )}

      <div>
        {isCreateAssistantOpen && (
          <div className="p-2">
            {isOneOffJob && (
              <div className="flex flex-col mb-3">
                <Typography.Paragraph2 className="text-neutral-900 mb-3">
                  Screening Job Name
                </Typography.Paragraph2>
                <TextField
                  className="text-neutral-900 mb-3"
                  id="name"
                  value={selectedJobConfig?.name || ''}
                  onChange={handleInputChange}
                />
              </div>
            )}
            <div className="flex flex-col mb-3">
              <Typography.Paragraph2 className="text-neutral-900 mb-3">
                Give a description for the resume screen:
              </Typography.Paragraph2>
              <TextArea
                className="text-neutral-900 mb-3"
                id="assistantDescription"
                value={selectedJobConfig?.assistantDescription || ''}
                onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => handleInputChange(e)}
                dynamic
                placeholder="You are a helpful job recruiter. I want to screen for..."
              />
            </div>
            <div className="flex flex-col mb-3">
              <Typography.Paragraph2 className="text-neutral-900 mb-1">
                Enter a list of true/false questions to ask the LLM about your candidate's resume.
                <b> Please end your question with "Please answer with 'True' or 'False' only."</b>:
              </Typography.Paragraph2>
              {(selectedJobConfig?.questionsToAskGPT || [''])?.map((question, index) => (
                <div key={index} className="flex flex-col">
                  <div className="flex flex-row text-neutral-900 justify-between items-center">
                    Question {index + 1}:
                    <Icon
                      className="cursor-pointer"
                      icon={['fas', 'trash-alt']}
                      size="md"
                      onClick={() => handleRemoveQuestion(index)}
                    />
                  </div>
                  <TextArea
                    className="text-neutral-900 mb-3"
                    name="questionsToAskGPT"
                    placeholder="Enter a True/False question for LLM"
                    value={question}
                    onChange={e => handleInputChange(e, index)}
                    dynamic
                  />
                </div>
              ))}
              <div className="flex items-center mt-0 mb-6">
                <Button variant="outline" className="flex items-center" onClick={handleAddQuestion}>
                  <BaseIcon contrastText edge="start" icon={'add'} variant="secondary" />
                  Add a Question
                </Button>
              </div>
              {selectedJobConfig?.questionsToAskGPT &&
                selectedJobConfig.questionsToAskGPT.length > 1 && (
                  <div className="flex flex-col mb-2">
                    <Typography.Paragraph2 className="text-neutral-900">
                      Candidate must pass:
                    </Typography.Paragraph2>
                    <RadioGroup
                      value={
                        selectedJobConfig?.requiresAllQuestionsPassed
                          ? 'allQuestions'
                          : 'atLeastOneQuestion'
                      }
                      onChange={(value: string) =>
                        handleRequiresAllQuestionsPassedChange(value === 'allQuestions')
                      }
                    >
                      <Radio
                        radioClassName="text-sm text-neutral-900 font-normal my-2 mx-2 p-0"
                        key="atLeastOneQuestion"
                        value="atLeastOneQuestion"
                        label="At least one question"
                      />
                      <Radio
                        radioClassName="text-sm text-neutral-900 font-normal my-1 mx-2 p-0"
                        key="allQuestions"
                        value="allQuestions"
                        label="All questions"
                      />
                    </RadioGroup>
                  </div>
                )}
            </div>
            {!isOneOffJob && (
              <Tooltip
                side="top"
                target={
                  <Button
                    className="flex items-center h-8"
                    onClick={() =>
                      openJobConfigTestDialog!(selectedJobConfig as ResumeScreeningConfig)
                    }
                    disabled={isTestDisabled}
                  >
                    Test this job
                  </Button>
                }
                text={
                  isTestDisabled &&
                  'Please enter a description and make sure all questions are complete.'
                }
              />
            )}
          </div>
        )}
      </div>
    </div>
  );
};
